import React, { useEffect, useState } from "react";
import { Button, Descriptions, Tag, Typography } from "antd";
import { useFetchReportAdminDetails, useFetchAuditReport } from "../hooks/useFetch";
import CaseDetailsModal from "../../../../components/CaseDetailsModal";
import AuditReportModal from "./AuditReportModal";
import PassReportModal from "./PassReportModal";

const { Title } = Typography;

/**
 * รายละเอียดเคสของหน้ารายงาน (Panel report) และหน้า Error Summary
 *
 * ส่วนแสดงเคสใช้ modal ตัวกลาง (components/CaseDetailsModal) — เดิมใช้ getInputDetails
 * ซึ่งไม่ส่งรูป/เหตุผล Reject-Inform มาเลย และ "Finish By" ที่แสดงจริง ๆ คือคนถือเคส
 *
 *   recordId  เลขรายงาน (reportCaseOutput.id) — ใช้กับปุ่ม Pass/Fail
 *   inputId   เลขเคส ถ้ารู้อยู่แล้ว (Error Summary / แถวของรายการที่มี input_id)
 *   report    แถวรายงานจากตาราง (แสดงข้อมูลการรายงานบนสุด)
 */
const DetailsModal = ({ visible, onClose, recordId, inputId, isDetail, report = null }) => {
  const { mutateAsync: getReportAdminDetails } = useFetchReportAdminDetails();
  const { mutateAsync: auditReport } = useFetchAuditReport();
  const [resolvedInputId, setResolvedInputId] = useState(null);

  // สำรอง: ถ้าแถวไม่มีเลขเคสมาด้วย (เช่น backend ยังไม่ deploy) ให้หาจากเลขรายงาน
  useEffect(() => {
    if (!visible) return;
    setResolvedInputId(null);
    if (!inputId && recordId) {
      getReportAdminDetails({ id: recordId }).then((res) => setResolvedInputId(res?.input_id || null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, recordId, inputId]);

  const [isAuditModalVisible, setIsAuditModalVisible] = useState(false);

  /* State for Pass Modal */
  const [isPassModalVisible, setIsPassModalVisible] = useState(false);

  const handlePass = () => {
    setIsPassModalVisible(true);
  };

  const handlePassConfirm = async (values) => {
    try {
      await auditReport({
        id: recordId,
        status: 'Pass',
        auditRemark: values.auditRemark
      });
      setIsPassModalVisible(false);
      onClose();
    } catch (error) {
      console.error("Error audit pass:", error);
    }
  };

  const handleAuditConfirm = async (values) => {
    try {
      await auditReport({
        id: recordId,
        status: 'Fail',
        auditRemarkType: values.auditRemarkType,
        auditRemark: values.auditRemark
      });
      setIsAuditModalVisible(false);
      onClose();
    } catch (error) {
      console.error("Error audit fail:", error);
    }
  };

  const handleFailClick = () => {
    setIsAuditModalVisible(true);
  };

  const caseId = inputId || resolvedInputId;

  const reportInfo = report ? (
    <>
      <Title level={4}>ข้อมูลการรายงาน</Title>
      <Descriptions bordered size="small" column={2} style={{ marginBottom: 24 }}>
        <Descriptions.Item label="รายงานโดย">{report.report_by || "-"}</Descriptions.Item>
        <Descriptions.Item label="วันที่รายงาน">{report.reported_date ? new Date(report.reported_date).toLocaleString("th-TH") : "-"}</Descriptions.Item>
        <Descriptions.Item label="ผลตรวจ">{report.status ? <Tag>{report.status}</Tag> : "-"}</Descriptions.Item>
        <Descriptions.Item label="หัวข้อที่ผิด">{report.audit_remark_type_label || "-"}</Descriptions.Item>
        <Descriptions.Item label="รายละเอียดที่รายงาน" span={2}>
          <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{report.report_remark || "-"}</div>
        </Descriptions.Item>
        {report.audit_remark && (
          <Descriptions.Item label="หมายเหตุผลตรวจ" span={2}>
            <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{report.audit_remark}</div>
          </Descriptions.Item>
        )}
      </Descriptions>
    </>
  ) : null;

  return (
    <>
      <CaseDetailsModal
        open={visible}
        onClose={onClose}
        caseId={caseId}
        extra={reportInfo}
        footer={
          !isDetail ? [
            <Button key="pass" type="primary" style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }} onClick={handlePass}>
              Pass
            </Button>,
            <Button key="fail" type="primary" danger onClick={handleFailClick}>
              Fail
            </Button>,
          ] : null
        }
      />
      <AuditReportModal
        visible={isAuditModalVisible}
        onClose={() => setIsAuditModalVisible(false)}
        onConfirm={handleAuditConfirm}
      />
      <PassReportModal
        visible={isPassModalVisible}
        onClose={() => setIsPassModalVisible(false)}
        onConfirm={handlePassConfirm}
      />
    </>
  );
};

export default DetailsModal;
